import { Link, useLocation } from 'react-router-dom';
import { Box, List, ListItem, Stack, Typography } from '@mui/material';
import Lock from '@mui/icons-material/Lock';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { ReactElement, useContext, useEffect, useMemo } from 'react';
import { TooltipHover } from '../../components/presentation-components/miscellaneous-components/tooltip-hover';
import { IRoleType } from '../../shared/services/user-details/user-details.model';
import { UserDetailsContext } from '../../context/user-details/user-details';
import { ILeftInnerNav, ILeftInnerNavPath, analyticsArray, emissionsArray, organisationArray, partnerLibraryArray, partnerSettingsArray, reductionArray, reportingArray, settingsArray, surveysArray } from '../../shared/data/navigation.data';
import { ScreenLabelsContext } from '../../context/screen-labels/screen-labels';
import { userHasRequiredRole as hasRequiredRole } from '../../shared/utils/role-utils';

export const InnerLeftNavbar = () => {
  const location = useLocation();
  const userDetailsContext = useContext(UserDetailsContext)
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const userDetails = userDetailsContext.userDetails

  // The data-import flow lives under /settings but can be entered from the Emissions menu
  // ("Import Emissions"). When it is, keep the Emissions menu (not Settings) across the whole flow.
  // The origin is a query flag on entry (context=emissions), made sticky across the flow's several
  // pages via sessionStorage. A bare wizard entry (no context, no in-progress fileUploadId) is a fresh
  // import, not a continuation, so it must NOT inherit a sticky emissions origin — otherwise a normal
  // import started via "Import More" or a reload would wrongly show the Emissions menu.
  const params = new URLSearchParams(location.search)
  const hasEmissionsContextParam = params.get('context') === 'emissions'
  const inImportFlow = location.pathname.startsWith('/settings/data-upload/import-data')
    || location.pathname.startsWith('/settings/data-upload/commit-progress')
  const isBareImportEntry = location.pathname === '/settings/data-upload/import-data'
    && !hasEmissionsContextParam && !params.get('fileUploadId')
  const emissionsImportContext = inImportFlow && !isBareImportEntry
    && (hasEmissionsContextParam || sessionStorage.getItem('importMenuContext') === 'emissions')

  // Persist / clear the sticky origin. Side effect only; the menu itself is derived synchronously
  // below (useMemo) so it is always correct on the first render and never flickers.
  useEffect(() => {
    if (hasEmissionsContextParam) {
      sessionStorage.setItem('importMenuContext', 'emissions')
    } else if (!inImportFlow || isBareImportEntry) {
      sessionStorage.removeItem('importMenuContext')
    }
  }, [hasEmissionsContextParam, inImportFlow, isBareImportEntry])

  // Derive the menu synchronously so the first render after any navigation is already correct in both
  // content and style — this removes the one-frame flicker (a passive effect-set array would lag).
  const array: ILeftInnerNav[] | undefined = useMemo(() => {
    if (emissionsImportContext) return emissionsArray

    // Orgs that customised the "product" screen label see their own term folded into the
    // split entries; the default ("Products") keeps the nav's static names, which
    // distinguish this register from the top-level Product Carbon Footprint area.
    const customProductsLabel = screenLabelsContext.getLabel('product', { plural: true, capitalize: 'first' })
    const hasCustomProductsLabel = !!customProductsLabel && customProductsLabel !== 'Products'
    const purchasedGoodsName = (name: string) =>
      hasCustomProductsLabel ? `Purchased & Capital ${customProductsLabel}` : name
    const soldProductsName = (name: string) =>
      hasCustomProductsLabel ? `Sold ${customProductsLabel}` : name

    if (location.pathname.startsWith('/organisation')) {
      return organisationArray.map((section) => ({
        ...section,
        category: screenLabelsContext.getLabel('organisation', { capitalize: 'all' }) || '',
        subcategory: section.subcategory
          .map((sub) => sub.to === '/organisation/subsidiaries'
            ? { ...sub, name: screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' }) }
            : sub.to === '/organisation/locations'
              ? { ...sub, name: screenLabelsContext.getLabel('location', { plural: true, capitalize: 'first' }) }
              : sub.to === '/organisation/vehicles'
                ? { ...sub, name: screenLabelsContext.getLabel('vehicle', { plural: true, capitalize: 'first' }) }
                : sub.to === '/organisation/projects'
                  ? { ...sub, name: screenLabelsContext.getLabel('project', { plural: true, capitalize: 'first' }) }
                  : sub.to === '/organisation/suppliers'
                    ? { ...sub, name: screenLabelsContext.getLabel('supplier', { plural: true, capitalize: 'first' }) }
                    : sub.to === '/organisation/products'
                      ? { ...sub, name: purchasedGoodsName(sub.name) }
                      : sub.to === '/organisation/sold-products'
                        ? { ...sub, name: soldProductsName(sub.name) }
                        : sub)
          .filter((sub) => sub.to !== '/organisation/subsidiaries' || (userDetails?.organisation?.enableSubsidiaries ?? false))
          .filter((s) => s.name !== 'Projects' || (userDetails?.organisation?.enableProjects ?? false))
          .filter((s) => s.name !== 'Renewable Energy Certificates' || (userDetails?.organisation?.enableRecs ?? false))
      }))
    }

    if (location.pathname.startsWith('/settings')) {
      return settingsArray.map((section) => ({
        ...section,
        subcategory: (section.category === 'Data import'
          ? section.subcategory.map((sub) =>
            sub.to === '/settings/data-upload/locations'
              ? { ...sub, name: `${screenLabelsContext.getLabel('location', { plural: true, capitalize: 'first' })} Data` }
              : sub.to === '/settings/data-upload/vehicles'
                ? { ...sub, name: `${screenLabelsContext.getLabel('vehicle', { plural: true, capitalize: 'first' })} Data` }
                : sub.to === '/settings/data-upload/products'
                  ? { ...sub, name: `${screenLabelsContext.getLabel('product', { plural: true, capitalize: 'first' })} Data` }
                  : sub
          )
          : section.subcategory
        ).filter((sub) => sub.to !== '/settings/company-settings/api-keys' || (userDetails?.organisation?.allowApiAccess ?? false))
      }))
    }

    if (location.pathname.startsWith('/emissions')) return emissionsArray
    if (location.pathname.startsWith('/reporting')) return reportingArray
    if (location.pathname.startsWith('/analytics')) return analyticsArray
    if (location.pathname.startsWith('/surveys')) return surveysArray
    if (location.pathname.startsWith('/partner-library')) return partnerLibraryArray
    if (location.pathname.startsWith('/partner-settings')) return partnerSettingsArray

    if (location.pathname.startsWith('/reduction-plan')) {
      return reductionArray.map((section) => ({
        ...section,
        subcategory: section.subcategory.filter((sub) => (
          sub.to !== '/reduction-plan/flag' || (userDetails?.organisation?.isFlagEnabled ?? false)
        )),
      }))
    }

    return undefined
  }, [location.pathname, emissionsImportContext, userDetails, screenLabelsContext]);


  function userHasRequiredRole(allowedRoles: IRoleType[]): boolean {
    return hasRequiredRole(userDetails, allowedRoles)
  }

  // Locked items (org has no name yet) still link to the overview as a fallback,
  // but say why instead of silently rewriting. Same copy as the top bar's locked
  // icons; conditional wrap so unlocked items carry no tooltip (see left-navbar).
  const withLockedTooltip = (item: ReactElement, isLocked: boolean) =>
    isLocked ? (
      <TooltipHover text='Finish setting up your company to unlock this' placement={'right'} backgroundColor={'common.white'}>
        {item}
      </TooltipHover>
    ) : item;

  return (
    <Box
      sx={{
        backgroundColor: 'lightGrey.light',
        boxSizing: 'border-box',
        width: '240px',
        borderRightColor: 'lightGrey.dark',
        borderRightWidth: '1px',
        borderRightStyle: 'solid',
        position: 'fixed',
        zIndex: 100,
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        pb: '200px',
      }}>
      {array &&
        <>
          {location.pathname.startsWith('/settings') && !emissionsImportContext ?
            (<Stack sx={{ p: 2, pl: 1 }}>
              <SimpleTreeView
                defaultExpandedItems={array.map((_, index) => `cat-${index}`)}
                sx={{
                  '& .MuiTreeItem-iconContainer svg': { color: 'text.secondary' },
                }}>
                {array.map((section: ILeftInnerNav, sectionIndex: number) => (
                  userHasRequiredRole(section.allowedRoles) && (
                    <TreeItem
                      key={`cat-${sectionIndex}`}
                      itemId={`cat-${sectionIndex}`}
                      sx={{
                        mt: sectionIndex === 0 ? 0 : 1, mb: 0,
                        '&& > .MuiTreeItem-content': {
                          pl: 1.5,
                          py: 0.75,
                          borderRadius: '10px',
                          backgroundColor: 'transparent',
                          '&:hover': { backgroundColor: 'transparent' },
                          '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'transparent' },
                          '&.Mui-focused': { backgroundColor: 'transparent' },
                          '&.Mui-focusVisible': { backgroundColor: 'transparent' },
                          '&.Mui-expanded': { backgroundColor: 'transparent' },
                          '&:not(:hover)': { backgroundColor: 'transparent' },
                        },
                      }}
                      label={
                        <Typography variant='subtitle1' color='text.secondary' sx={{ textTransform: 'uppercase', my: 0, fontSize: '0.9rem', lineHeight: 1.2, whiteSpace: 'nowrap', minHeight: '24px', display: 'flex', alignItems: 'center' }}>
                          {section.category || ''}
                        </Typography>
                      }>
                      {section.subcategory.filter((setting) => !setting.allowedRoles || userHasRequiredRole(setting.allowedRoles)).map((setting: ILeftInnerNavPath) => {
                        const isActive = setting.isActive(location.pathname);
                        const destination = userDetails?.organisation.name == null ? '/settings/company-settings/overview' : setting.to;
                        const isLocked = userDetails?.organisation.name == null && !isActive;
                        return (
                          <TreeItem key={setting.to} itemId={setting.to}
                            label={withLockedTooltip(
                              <Link
                                to={destination}
                                style={{
                                  textDecoration: 'none',
                                  cursor: isLocked ? 'default' : 'pointer',
                                  color: 'inherit',
                                  display: 'block',
                                  width: '100%'
                                }}
                              >
                                <Typography
                                  variant='body1'
                                  sx={{ color: isLocked ? 'text.disabled' : 'text.primary', minHeight: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                >
                                  {setting.name}
                                  {isLocked && <Lock sx={{ fontSize: '16px', color: 'text.disabled' }} />}
                                </Typography>
                              </Link>,
                              isLocked
                            )}
                            sx={{
                              '&& > .MuiTreeItem-content': {
                                pl: 1.5,
                                py: 0.75,
                                borderRadius: '10px',
                                backgroundColor: isActive ? 'secondary.main' : 'lightGrey.light',
                                '&:hover': {
                                  backgroundColor: isLocked ? 'lightGrey.light' : isActive ? 'secondary.main' : 'lightGrey.main',
                                },
                                ...(isActive && {
                                  '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'secondary.main' },
                                  '&.Mui-focused': { backgroundColor: 'secondary.main' },
                                  '&.Mui-focusVisible': { backgroundColor: 'secondary.main' },
                                }),
                              },
                              '& .MuiTreeItem-label': { width: '100%' },
                            }}
                          />
                        );
                      })}
                    </TreeItem>
                  )
                ))}
              </SimpleTreeView>
            </Stack>) :
            <Stack sx={{ p: 3 }}>
              {array.map((object: ILeftInnerNav, index: number) => (
                userHasRequiredRole(object.allowedRoles) &&
                <Box key={index}>
                  <Typography variant='subtitle1' color='text.secondary' sx={{ textTransform: 'uppercase', minHeight: '24px', display: 'flex', alignItems: 'center' }}>
                    {object.category || ''}
                  </Typography>
                  <List sx={{ pb: 2 }}>
                    {object.subcategory.filter((setting) => !setting.allowedRoles || userHasRequiredRole(setting.allowedRoles)).map((setting: ILeftInnerNavPath) => {
                      const isActive = setting.isActive(location.pathname)
                      const isLocked = userDetails?.organisation.name == null && !isActive
                      const destination = userDetails?.organisation.name == null ? '/settings/company-settings/overview' : setting.to
                      return (
                        <Box key={setting.to}>
                          {withLockedTooltip(
                            <Link to={destination} style={{ textDecoration: 'none', cursor: isLocked ? 'default' : 'pointer' }}>
                              <ListItem sx={{
                                pl: 2, borderRadius: '10px',
                                '&:hover': {
                                  backgroundColor: isLocked ? 'lightGrey.light' : isActive ? 'secondary.main' : 'lightGrey.main'
                                },
                                backgroundColor: isActive ? 'secondary.main' : 'lightGrey.light'
                              }}>
                                <Typography
                                  variant='body1'
                                  sx={{ color: isLocked ? 'text.disabled' : 'text.primary', minHeight: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                                >
                                  {setting.name}
                                  {isLocked && <Lock sx={{ fontSize: '16px', color: 'text.disabled' }} />}
                                </Typography>
                              </ListItem>
                            </Link>,
                            isLocked
                          )}
                        </Box>
                      )
                    })}
                  </List>
                </Box>))
              }
            </Stack >
          }
        </>}
    </Box >
  )
}

